import {useEffect} from 'react';
import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {v4 as uuid} from 'uuid';

import {combineNotes} from './annotationMerge';
import {resetPaintCoordinator} from './annotationPaintCoordinator';
import type {
  Annotation,
  AnnotationMerge,
  AnnotationType,
  HighlightColor,
  TextAnchor
} from './annotationTypes';

export interface AnnotationModeState {
  isAnnotationModeOn: boolean;
  annotationsByRegionId: Record<string, Annotation[]>;
  defaultColor: HighlightColor;
  defaultType: AnnotationType;
  /** Regions currently mounted with non-empty text. False is never stored — the key is deleted. */
  annotatableContentByRegionId: Record<string, true>;
}

interface AnnotationModeActions {
  toggleAnnotationMode: () => void;
  addAnnotation: (anchor: TextAnchor) => string;
  mergeAnnotations: (merge: AnnotationMerge) => void;
  setHighlightColor: (regionId: string, id: string, color: HighlightColor) => void;
  setAnnotationUnderline: (regionId: string, id: string) => void;
  setAnnotationNote: (regionId: string, id: string, note: string) => void;
  removeAnnotation: (regionId: string, id: string) => void;
  setRegionHasAnnotatableContent: (regionId: string, hasContent: boolean) => void;
  reset: () => void;
}

const initialState: AnnotationModeState = {
  isAnnotationModeOn: false,
  annotationsByRegionId: {},
  defaultColor: 'yellow',
  defaultType: 'highlight',
  annotatableContentByRegionId: {}
};

export const useAnnotationModeStore = create<AnnotationModeState & AnnotationModeActions>()(
  devtools(
    (set) => ({
      ...initialState,

      toggleAnnotationMode: () => {
        set(
          (state) => ({isAnnotationModeOn: !state.isAnnotationModeOn}),
          false,
          'toggleAnnotationMode'
        );
      },

      // The anchor is authoritative: its regionId names the bucket.
      addAnnotation: (anchor: TextAnchor) => {
        const id = uuid();
        set(
          (state) => ({
            annotationsByRegionId: {
              ...state.annotationsByRegionId,
              [anchor.regionId]: [
                ...(state.annotationsByRegionId[anchor.regionId] ?? []),
                {id, anchor, color: state.defaultColor, type: state.defaultType}
              ]
            }
          }),
          false,
          'addAnnotation'
        );
        return id;
      },

      // Absorbed notes are read at apply time, not baked into the payload: a note
      // flushed between the merge gesture and its confirmation still counts.
      mergeAnnotations: ({intoId, absorbedIds, anchor}: AnnotationMerge) => {
        set(
          (state) => {
            const annotations = state.annotationsByRegionId[anchor.regionId] ?? [];
            const absorbedNotes = absorbedIds.map(
              (absorbedId) => annotations.find((annotation) => annotation.id === absorbedId)?.note
            );
            return {
              annotationsByRegionId: {
                ...state.annotationsByRegionId,
                [anchor.regionId]: annotations
                  .filter((annotation) => !absorbedIds.includes(annotation.id))
                  .map((annotation) =>
                    annotation.id === intoId
                      ? {...annotation, anchor, note: combineNotes(annotation.note, absorbedNotes)}
                      : annotation
                  )
              }
            };
          },
          false,
          'mergeAnnotations'
        );
      },

      // Recolor one annotation as a highlight and make that color/type the default for new ones.
      setHighlightColor: (regionId: string, id: string, color: HighlightColor) => {
        set(
          (state) => ({
            defaultColor: color,
            defaultType: 'highlight',
            annotationsByRegionId: {
              ...state.annotationsByRegionId,
              [regionId]: (state.annotationsByRegionId[regionId] ?? []).map((annotation) =>
                annotation.id === id ? {...annotation, color, type: 'highlight'} : annotation
              )
            }
          }),
          false,
          'setHighlightColor'
        );
      },

      // Switch one annotation to underline (mutually exclusive with color) and make it the default.
      setAnnotationUnderline: (regionId: string, id: string) => {
        set(
          (state) => ({
            defaultType: 'underline',
            annotationsByRegionId: {
              ...state.annotationsByRegionId,
              [regionId]: (state.annotationsByRegionId[regionId] ?? []).map((annotation) =>
                annotation.id === id ? {...annotation, type: 'underline'} : annotation
              )
            }
          }),
          false,
          'setAnnotationUnderline'
        );
      },

      // A blank note is a deleted note: the field goes absent, never ''.
      setAnnotationNote: (regionId: string, id: string, note: string) => {
        set(
          (state) => ({
            annotationsByRegionId: {
              ...state.annotationsByRegionId,
              [regionId]: (state.annotationsByRegionId[regionId] ?? []).map((annotation) =>
                annotation.id === id
                  ? {...annotation, note: note.trim() ? note : undefined}
                  : annotation
              )
            }
          }),
          false,
          'setAnnotationNote'
        );
      },

      removeAnnotation: (regionId: string, id: string) => {
        set(
          (state) => ({
            annotationsByRegionId: {
              ...state.annotationsByRegionId,
              [regionId]: (state.annotationsByRegionId[regionId] ?? []).filter(
                (annotation) => annotation.id !== id
              )
            }
          }),
          false,
          'removeAnnotation'
        );
      },

      setRegionHasAnnotatableContent: (regionId: string, hasContent: boolean) => {
        set(
          (state) => {
            const isRegistered = Boolean(state.annotatableContentByRegionId[regionId]);
            if (hasContent === isRegistered) {
              return state;
            }
            const next = {...state.annotatableContentByRegionId};
            if (hasContent) {
              next[regionId] = true;
            } else {
              delete next[regionId];
            }
            return {annotatableContentByRegionId: next};
          },
          false,
          'setRegionHasAnnotatableContent'
        );
      },

      reset: () => {
        // Or stale highlights would outlive an emptied store.
        resetPaintCoordinator();
        set(initialState, false, 'reset');
      }
    }),
    {name: 'AnnotationModeStore'}
  )
);

/**
 * Hook that resets the annotation mode store on mount and unmount.
 * Call this at the practice-session level (e.g. AssignmentPracticeView), not
 * inside the per-question subtree — PracticeView remounts on every question
 * navigation and the mode must stay on across questions.
 */
export function useAnnotationModeReset() {
  const reset = useAnnotationModeStore((state) => state.reset);

  useEffect(() => {
    reset();
    return () => {
      reset();
    };
  }, [reset]);
}
